import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addDiscountRequest, removeDiscountRequest } from '@modules/subscriptions/core/actions';
import {
  getCurrencyCodeSelector,
  getDefaultSubscriptionSelector,
  getSubscriptionLevelDiscountsSelector,
  getErrorSelector,
} from '@modules/subscriptions/core/selectors';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';
import { KTSVG, toAbsoluteUrl } from '@helpers/index';
import { FormattedMessage, useIntl } from 'react-intl';

export const Discount = ({ disabled = false }: { disabled?: boolean }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);
  const discounts = useSelector(getSubscriptionLevelDiscountsSelector);
  const error = useSelector(getErrorSelector);
  const currencySymbol = useSelector(getCurrencyCodeSelector);

  const [discountCode, setDiscountCode] = useState<string>('');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountCode(event.target.value);
  };

  const handleAddDiscount = () => {
    if (disabled) return;
    const code = discountCode.trim();
    if (!code) return;
    dispatch(
      addDiscountRequest({
        id: defaultSubscription?.id!,
        params: {
          code,
        },
      }),
    );
    setDiscountCode('');
  };

  const handleRemoveDiscount = (discountShopifyId: string) => {
    if (disabled) return;
    dispatch(
      removeDiscountRequest({
        id: defaultSubscription?.id!,
        params: {
          discountShopifyId,
        },
      }),
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAddDiscount();
    }
  };

  return (
    <div className="discount-wrapper">
      {discounts.length > 0 && (
        <div className="font-medium mb-[.9rem]">
          <div className="flex items-center gap-[.5rem] flex-wrap">
            <p>
              <FormattedMessage id="DISCOUNT.LABEL" />
            </p>
            {discounts.map((discount) => (
              <span key={discount.shopifyId ?? discount.title} className="discount-wrapper-text">
                <p>{formatDiscountTitle(intl, discount.title, currencySymbol)}</p>
                {discount.shopifyId && !disabled && (
                  <button
                    type="button"
                    aria-label={intl.formatMessage({ id: 'DISCOUNT.REMOVE' })}
                    onClick={() => handleRemoveDiscount(discount.shopifyId!)}>
                    <KTSVG path={toAbsoluteUrl('/media/svg/general/remove.svg')} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="relative flex">
          <label className="discount-wrapper-label -top-[1rem] bg-light-100">
            <FormattedMessage id="DISCOUNT.CODE_LABEL" />
          </label>
          <input
            type="text"
            placeholder={
              // Two literal ids rather than a ternary inside formatMessage, so
              // i18n-check can see both.
              discounts.length > 0
                ? intl.formatMessage({ id: 'DISCOUNT.PLACEHOLDER_MORE' })
                : intl.formatMessage({ id: 'DISCOUNT.PLACEHOLDER' })
            }
            value={discountCode}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={disabled}
            className="w-full discount-wrapper-input bg-light-100 disabled:opacity-50 disabled:cursor-not-allowed"
          />

          <button
            className="discount-handler-btn disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddDiscount}
            disabled={disabled}>
            <FormattedMessage id="DISCOUNT.APPLY" />
          </button>
        </div>
        {disabled ? (
          <div className="discount-info-msg">
            <FormattedMessage id="DISCOUNT.ORDER_IN_PROGRESS" />
          </div>
        ) : (
          error && (
            <div className="discount-error-msg">
              {intl.formatMessage({ id: 'DISCOUNT.INVALID_CODE' })}
            </div>
          )
        )}
      </div>
    </div>
  );
};
